/**
 * Frame-buffer materialization.
 *
 * The stack-frame pass deletes the `fp = __stack_pointer - N` allocation as prologue
 * boilerplate. That is correct only when the frame is pure spill space and `fp` is dead
 * afterwards. When the frame's address escapes — a scratch slot `fp + k` is handed to a
 * callee, or the frame is used as a buffer — `fp` still has live reads, and deleting only
 * its definition leaves it used-before-assignment.
 *
 * This pass detects that case (the base is referenced but never assigned) and represents
 * the frame as a real local buffer: it declares `unsigned char frame[N];` (in the emitter)
 * and rewrites every frame-relative address into a `frame_slot` node, so `memory[fp + k]`
 * becomes `*(int*)&frame[k]` and a bare `fp + k` argument becomes `&frame[k]`.
 */

import { Expression, Statement, LoweredNode } from './ExpressionIR';
import { collectReferencedVars, collectAssignTargets } from './LoweredEmitter';

export interface FrameBuffer {
  name: string;
  size: number;
}

export interface FrameBufferResult {
  node: LoweredNode;
  buffer: FrameBuffer | null;
}

/**
 * Rewrites the frame base into a local byte buffer when it escapes (is read but never
 * assigned after prologue removal). Returns the rewritten tree and the buffer to declare,
 * or the original tree and a null buffer when there is nothing to materialize.
 */
export function materializeFrameBuffer(
  node: LoweredNode,
  frameVarName: string | null,
  frameSize: number,
  paramNames: Set<string>,
): FrameBufferResult {
  if (!frameVarName || frameSize <= 0) {
    return { node, buffer: null };
  }
  const referenced = new Set<string>();
  collectReferencedVars(node, referenced);
  const assigned = new Set<string>();
  collectAssignTargets(node, assigned);
  // The frame base, plus any variable that only ever holds a copy of it (a loop-invariant
  // base shuffled through phi-elimination copies, e.g. `ptr2 = ptr; … ptr = ptr2`).
  const aliases = computeFrameAliases(node, frameVarName);
  // Materialize when the base escapes: either it is read with no definition left (the clean
  // case), or it survives only as a copy class (every assignment is an intra-class copy). A
  // base with a genuine non-copy assignment is a reused slot — already defined, leave it.
  const baseHasRealAssignment = aliases.size === 1 && assigned.has(frameVarName);
  const anyReferenced = [...aliases].some(name => referenced.has(name));
  if (baseHasRealAssignment || !anyReferenced) {
    return { node, buffer: null };
  }
  const bufferName = chooseBufferName(referenced, assigned, paramNames);
  const rewritten = new FrameBufferRewriter(aliases, bufferName).rewrite(node);
  return { node: rewritten, buffer: { name: bufferName, size: frameSize } };
}

// The frame base's copy-equivalence class: start from the base and add any variable whose
// every assignment is a copy from a class member, to a fixpoint. Returns just the base when
// any member has a non-copy assignment (then the base is a genuinely reused slot, not a pure
// frame pointer, and must not be turned into a buffer).
function computeFrameAliases(node: LoweredNode, frameVarName: string): Set<string> {
  const copySources = new Map<string, Set<string>>();
  const hasNonCopyAssignment = new Set<string>();
  collectAssignmentKinds(node, copySources, hasNonCopyAssignment);

  const aliases = new Set<string>([frameVarName]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const [target, sources] of copySources) {
      if (!aliases.has(target) && !hasNonCopyAssignment.has(target) && [...sources].every(source => aliases.has(source))) {
        aliases.add(target);
        changed = true;
      }
    }
  }
  for (const alias of aliases) {
    const sources = copySources.get(alias) ?? new Set<string>();
    if (hasNonCopyAssignment.has(alias) || [...sources].some(source => !aliases.has(source))) {
      return new Set<string>([frameVarName]);
    }
  }
  return aliases;
}

function chooseBufferName(...nameSets: Set<string>[]): string {
  const taken = new Set<string>();
  for (const names of nameSets) {
    for (const name of names) {
      taken.add(name);
    }
  }
  if (!taken.has('frame')) {
    return 'frame';
  }
  let suffix = 2;
  while (taken.has(`frame${suffix}`)) {
    suffix++;
  }
  return `frame${suffix}`;
}

// Records, for each assigned variable, the set of variables it is copied from (`x = y`) and
// whether it has any non-copy assignment (a const, a computed value, a call result).
export function collectAssignmentKinds(node: LoweredNode, copySources: Map<string, Set<string>>, hasNonCopyAssignment: Set<string>): void {
  const noteAssign = (target: string, value: Expression): void => {
    if (value.kind === 'var') {
      let sources = copySources.get(target);
      if (!sources) {
        sources = new Set<string>();
        copySources.set(target, sources);
      }
      sources.add(value.name);
    } else {
      hasNonCopyAssignment.add(target);
    }
  };
  const visitStatement = (statement: Statement): void => {
    if (statement.kind === 'assign') {
      noteAssign(statement.target, statement.value);
    } else if ((statement.kind === 'call' || statement.kind === 'call_indirect') && statement.result) {
      hasNonCopyAssignment.add(statement.result);
    }
  };
  const walk = (current: LoweredNode): void => {
    switch (current.kind) {
      case 'sequence': current.children.forEach(walk); break;
      case 'block': current.body.forEach(visitStatement); break;
      case 'if': walk(current.thenBody); if (current.elseBody) { walk(current.elseBody); } break;
      case 'while': case 'do_while': case 'labeled_block': walk(current.body); break;
      case 'for': visitStatement(current.init); visitStatement(current.increment); walk(current.body); break;
      case 'switch': current.cases.forEach(caseEntry => walk(caseEntry.body)); walk(current.defaultBody); break;
    }
  };
  walk(node);
}

/** Rewrites every frame-relative address (the base or any of its copy aliases) into a `frame_slot`. */
class FrameBufferRewriter {
  constructor(
    private readonly frameAliases: Set<string>,
    private readonly bufferName: string,
  ) {}

  rewrite(node: LoweredNode): LoweredNode {
    return this.mapNode(node);
  }

  private isAlias(expr: Expression): boolean {
    return expr.kind === 'var' && this.frameAliases.has(expr.name);
  }

  // The byte offset of a frame-relative address, or null when the expression does not
  // address the frame: an alias itself is offset 0, and `alias + x` / `x + alias` is x.
  private frameIndex(expr: Expression): Expression | null {
    if (this.isAlias(expr)) {
      return { kind: 'const', value: 0, type: 'i32' };
    }
    if (expr.kind === 'binary' && expr.op === '+') {
      if (this.isAlias(expr.left)) {
        return expr.right;
      }
      if (this.isAlias(expr.right)) {
        return expr.left;
      }
    }
    return null;
  }

  private addOffset(index: Expression, offset: number): Expression {
    if (offset === 0) {
      return index;
    }
    if (index.kind === 'const' && typeof index.value === 'number') {
      return { kind: 'const', value: index.value + offset, type: 'i32' };
    }
    return { kind: 'binary', op: '+', left: index, right: { kind: 'const', value: offset, type: 'i32' } };
  }

  private mapExpression(expr: Expression): Expression {
    // A load dereferences a frame slot (`*(T*)&frame[k]`); the load's own memarg offset
    // folds into the slot index so the emitter renders a single subscript.
    if (expr.kind === 'load') {
      const index = this.frameIndex(expr.address);
      if (index !== null) {
        return { kind: 'load', address: { kind: 'frame_slot', index: this.addOffset(index, expr.offset) }, offset: 0, loadType: expr.loadType };
      }
      return { kind: 'load', address: this.mapExpression(expr.address), offset: expr.offset, loadType: expr.loadType };
    }
    // Any other frame-relative expression is the address `&frame[k]`.
    const index = this.frameIndex(expr);
    if (index !== null) {
      return { kind: 'frame_slot', index };
    }
    return this.mapChildren(expr);
  }

  private mapChildren(expr: Expression): Expression {
    switch (expr.kind) {
      case 'binary':
        return { kind: 'binary', op: expr.op, left: this.mapExpression(expr.left), right: this.mapExpression(expr.right) };
      case 'compare':
        return { kind: 'compare', op: expr.op, left: this.mapExpression(expr.left), right: this.mapExpression(expr.right) };
      case 'unary':
        return { kind: 'unary', op: expr.op, operand: this.mapExpression(expr.operand) };
      case 'convert':
        return { kind: 'convert', op: expr.op, operand: this.mapExpression(expr.operand) };
      case 'select':
        return { kind: 'select', condition: this.mapExpression(expr.condition), trueVal: this.mapExpression(expr.trueVal), falseVal: this.mapExpression(expr.falseVal) };
      case 'call':
        return { kind: 'call', name: expr.name, args: expr.args.map(arg => this.mapExpression(arg)) };
      case 'call_indirect':
        return { kind: 'call_indirect', tableIndex: this.mapExpression(expr.tableIndex), args: expr.args.map(arg => this.mapExpression(arg)) };
      case 'field_access':
        return { kind: 'field_access', base: this.mapExpression(expr.base), offset: expr.offset };
      default:
        return expr;
    }
  }

  private mapStatement(statement: Statement): Statement | null {
    // An intra-class copy (`alias = otherAlias`) just shuffles the frame base; once every
    // alias is the buffer it is a no-op (`&frame[0] = &frame[0]`), so drop it.
    if (statement.kind === 'assign' && this.frameAliases.has(statement.target) && this.isAlias(statement.value)) {
      return null;
    }
    switch (statement.kind) {
      case 'store': {
        const index = this.frameIndex(statement.address);
        if (index !== null) {
          return { kind: 'store', address: { kind: 'frame_slot', index: this.addOffset(index, statement.offset) }, offset: 0, storeType: statement.storeType, value: this.mapExpression(statement.value) };
        }
        return { kind: 'store', address: this.mapExpression(statement.address), offset: statement.offset, storeType: statement.storeType, value: this.mapExpression(statement.value) };
      }
      case 'assign':
        return { kind: 'assign', target: statement.target, type: statement.type, value: this.mapExpression(statement.value) };
      case 'call':
        return { kind: 'call', name: statement.name, args: statement.args.map(arg => this.mapExpression(arg)), result: statement.result };
      case 'call_indirect':
        return { kind: 'call_indirect', tableIndex: this.mapExpression(statement.tableIndex), args: statement.args.map(arg => this.mapExpression(arg)), result: statement.result };
      case 'global_set':
        return { kind: 'global_set', name: statement.name, value: this.mapExpression(statement.value) };
      case 'return':
        return { kind: 'return', values: statement.values.map(value => this.mapExpression(value)) };
      case 'expr':
        return { kind: 'expr', value: this.mapExpression(statement.value) };
      default:
        return statement;
    }
  }

  private mapNode(node: LoweredNode): LoweredNode {
    switch (node.kind) {
      case 'sequence':
        return { kind: 'sequence', children: node.children.map(child => this.mapNode(child)) };
      case 'block':
        return { kind: 'block', body: node.body.map(statement => this.mapStatement(statement)).filter((statement): statement is Statement => statement !== null) };
      case 'if':
        return { kind: 'if', condition: this.mapExpression(node.condition), thenBody: this.mapNode(node.thenBody), elseBody: node.elseBody ? this.mapNode(node.elseBody) : null };
      case 'while':
        return { kind: 'while', condition: node.condition ? this.mapExpression(node.condition) : null, body: this.mapNode(node.body) };
      case 'do_while':
        return { kind: 'do_while', body: this.mapNode(node.body), condition: this.mapExpression(node.condition) };
      case 'for':
        // init/increment are loop-counter assigns, never frame-base copies, so they never drop.
        return { kind: 'for', init: this.mapStatement(node.init) ?? node.init, condition: this.mapExpression(node.condition), increment: this.mapStatement(node.increment) ?? node.increment, body: this.mapNode(node.body) };
      case 'switch':
        return { kind: 'switch', selector: this.mapExpression(node.selector), cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: this.mapNode(caseEntry.body) })), defaultBody: this.mapNode(node.defaultBody) };
      case 'labeled_block':
        return { kind: 'labeled_block', label: node.label, body: this.mapNode(node.body) };
      case 'return':
        return { kind: 'return', values: node.values.map(value => this.mapExpression(value)) };
      default:
        return node;
    }
  }
}
